'use client';
import { FC, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Autocomplete,
  Chip,
  Link as MuiLink,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { useRouter } from 'next/navigation';
import { createDataPackageSubmitValidation, toDataPackageSubmitValidationInput } from './dataPackageValidation';
import { DebouncedTextField } from '@/components/common/DebouncedTextField';
import { PrivilegeEnum, DataPackageDto, StatusDto } from '@/api/ApiClient';
import type { NodeDataType } from '@/types/tree';
import { log } from '@/lib/utils/logger';
import DataPackageFooter from '@/components/details/DataPackageFooter';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import DetailField from '@/components/common/DetailField';
import { UserLinks } from '@/components/common/UserLink';
import { OptionKey } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { formatCollaboratorLabel } from '@/lib/options/userUtils';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useDataPackageData, useIsDataModified } from '@/hooks/useTreeDetailSubscriptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useBackendStore } from '@/stores/backendStore';
import { useSubmitWithValidation } from '@/hooks/useSubmitWithValidation';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useAuthStore } from '@/stores/authStore';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createDataPackageSaveValidation } from '@/lib/validation/saveValidationBuilders';
import { toUpdateDataPackageRequest } from '@/lib/entity/entitySaveUtils';
import { isSnapshotBuildStaleForRestart } from '@/lib/utils/snapshotRestartEligibility';
import { buildPublicDataPackageUrl, stripDoiPrefix, toDoiHref } from '@/lib/utils/doiLinks';

import { useSnapshotProgress } from '@/hooks/useSnapshotProgress';
import { useActionExecutor } from '@/hooks/useNodeActions';
import { getDataPackageApiId } from '@/lib/tree/treeUtils';

export const selectOptions: OptionKey[] = ['synthesisInstruments', 'characterizationInstruments', 'staff', 'users'];

type InstrumentDisplayRef = { id?: string; name?: string; doi?: string };

/** Look up each package instrument in instrumentOptions so display mode has name and DOI. */
function resolveSelectedInstruments(
  selectedInstruments: InstrumentDisplayRef[] | undefined,
  instrumentOptions: InstrumentDisplayRef[] | undefined
): InstrumentDisplayRef[] {
  return (selectedInstruments ?? []).map((selected) => {
    const option = instrumentOptions?.find((i) => i.id === selected.id);
    return {
      id: selected.id,
      name: selected.name ?? option?.name,
      doi: selected.doi ?? option?.doi,
    };
  });
}

function InstrumentReadonlyList({ instruments }: { instruments: InstrumentDisplayRef[] }) {
  if (instruments.length === 0) {
    return <>None</>;
  }
  return (
    <>
      {instruments.map((instrument, index) => (
        <Box key={instrument.id ?? index} component="div" sx={index < instruments.length - 1 ? { mb: 0.5 } : undefined}>
          <span>
            {instrument.id} - {instrument.name}
          </span>
          {instrument.doi ? (
            <>
              {' ('}
              <MuiLink href={toDoiHref(instrument.doi)} target="_blank" rel="noopener noreferrer" underline="hover">
                {stripDoiPrefix(instrument.doi)}
              </MuiLink>
              )
            </>
          ) : null}
        </Box>
      ))}
    </>
  );
}

function DoiReadonlyLink({ doi }: { doi: string }) {
  return (
    <MuiLink
      href={toDoiHref(doi)}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, wordBreak: 'break-all' }}
    >
      {stripDoiPrefix(doi)}
      <OpenInNew fontSize="small" />
    </MuiLink>
  );
}

/** Server fields to merge after snapshot completes when the row is dirty (avoid overwriting in-progress edits). */
function snapshotPostCompleteServerOverlay(dto: DataPackageDto): Partial<DataPackageDto> {
  return {
    percentSnapshotDone: dto.percentSnapshotDone,
    snapshotInProgress: dto.snapshotInProgress,
    isSnapshot: dto.isSnapshot,
    status: dto.status,
    prePublishId: dto.prePublishId,
    lastChangedDate: dto.lastChangedDate,
    lastChangedByUser: dto.lastChangedByUser,
  };
}

const DataPackageDetails: FC = () => {
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const { resolveUser } = useUserResolution();
  const { showSnackbar } = useSnackbar();

  const {
    updateDataPartial,
    updateData,
    mergeEntityFromServer,
    setDataModified,
    isDataModified,
    reloadNode,
    invalidateChildrenCache,
    clearChildrenLoaded,
    loadChildren,
    expandNode,
  } = useTree();
  const { selectedNode } = useTreeSelection();
  const backend = useBackendStore.getState().getBackend();
  const router = useRouter();
  const systemConfig = useSystemConfig();
  const { isAuthorized } = useAuthStore();
  const currentUserId = useAuthStore((s) => s.currentUser?.id);
  const executeActionForNode = useActionExecutor();

  const [generatingShareLink, setGeneratingShareLink] = useState(false);

  const dataPackage = useDataPackageData(selectedNode);

  /** Tree `nodeId` for `mergeEntityFromServer` / store lookups (stable across async poll callbacks). */
  const selectedTreeNodeIdRef = useRef<string | null>(null);
  /** `dataId` for `isDataModified` checks in async completion (tree modified flag is keyed by dataId). */
  const selectedDataIdRef = useRef<string | null>(null);
  /** Route/API key for data-package GET (numeric id, DOI, or prePublishId from tree dataId). */
  const latestDataPackageApiIdRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    selectedTreeNodeIdRef.current = selectedNode?.nodeId ?? null;
    selectedDataIdRef.current = selectedNode?.dataId ?? null;
    latestDataPackageApiIdRef.current = selectedNode?.dataId ? getDataPackageApiId(selectedNode.dataId) : null;
  }, [selectedNode?.nodeId, selectedNode?.dataId]);

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const [isManuallyEditing, setIsManuallyEditing] = useState(false);
  const isReadOnly = !isModified && !isManuallyEditing;

  // Validation state
  const [titleError, setTitleError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [keywordsError, setKeywordsError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { submitWithValidation } = useSubmitWithValidation();

  /** Each poll `GET` merges snapshot progress flags into the tree store (server-only fields). */
  const mergeSnapshotPollDtoIntoStore = useCallback(
    (pkg: DataPackageDto) => {
      const nodeId = selectedTreeNodeIdRef.current;
      if (!nodeId) return;
      const partial: Partial<DataPackageDto> = {};
      if (pkg.percentSnapshotDone !== undefined) partial.percentSnapshotDone = pkg.percentSnapshotDone;
      if (pkg.snapshotInProgress !== undefined) partial.snapshotInProgress = pkg.snapshotInProgress;
      if (Object.keys(partial).length === 0) return;
      mergeEntityFromServer(nodeId, partial as Partial<NodeDataType>);
    },
    [mergeEntityFromServer]
  );

  /**
   * After polling reports snapshot completion:
   * - **Clean row** — same refresh as an explicit Reload without prompting: `reloadNode` + child reload (children may
   *   switch to snapshot copies). No confirm dialog: unsaved edits are assumed to mark the row dirty; if not, that is a separate bug.
   * - **Dirty row** — one GET + `mergeEntityFromServer` overlay only (snapshot/status/audit fields) so local edits stay.
   *   Snackbar explains that a full reload was skipped due to unsaved changes.
   */
  const refreshStoreAfterSnapshotCompletes = useCallback(async () => {
    const nodeId = selectedTreeNodeIdRef.current;
    const apiId = latestDataPackageApiIdRef.current;
    const dataId = selectedDataIdRef.current;
    if (!nodeId) return;
    const dirty = dataId ? isDataModified(dataId) : false;
    try {
      if (dirty) {
        if (!apiId) return;
        const updatedData = await backend.data.get(apiId);
        mergeEntityFromServer(nodeId, snapshotPostCompleteServerOverlay(updatedData) as Partial<NodeDataType>);
        showSnackbar(
          'Snapshot finished on the server, but a full reload was skipped because this package has unsaved changes. Snapshot and status fields were updated; save or cancel to refresh the rest.',
          'info'
        );
      } else {
        invalidateChildrenCache(nodeId);
        await reloadNode(nodeId, backend);
        clearChildrenLoaded(nodeId);
        await loadChildren(nodeId, backend);
        expandNode(nodeId);
        showSnackbar('Snapshot created successfully.', 'success');
      }
    } catch (error) {
      log.error('[DataPackageDetails] Failed to reload data package after snapshot:', error);
    }
  }, [
    backend,
    clearChildrenLoaded,
    expandNode,
    invalidateChildrenCache,
    isDataModified,
    loadChildren,
    mergeEntityFromServer,
    reloadNode,
    showSnackbar,
  ]);

  const notifySnapshotPollError = useCallback(
    (error: unknown) => {
      log.error('[DataPackageDetails] Snapshot progress polling error:', error);
      showSnackbar('Error checking snapshot progress', 'error');
    },
    [showSnackbar]
  );

  // Snapshot polling: orchestration effect only starts/stops timers; DTO merges use `mergeEntityFromServer` (no dirty flag).
  const { startPolling, stopPolling } = useSnapshotProgress({
    onPollPackage: mergeSnapshotPollDtoIntoStore,
    onComplete: refreshStoreAfterSnapshotCompletes,
    onError: notifySnapshotPollError,
  });

  /**
   * Which package id we last wired polling for — when selection / package changes, stop and reset session keys.
   * `snapshotPollSessionRef` avoids restarting `startPolling` on every percent tick from store merges.
   */
  const orchestratedPackageApiIdRef = useRef<string | null>(null);
  const snapshotPollSessionRef = useRef<string | null>(null);
  useEffect(() => {
    const apiId = selectedNode?.dataId ? getDataPackageApiId(selectedNode.dataId) : null;
    if (orchestratedPackageApiIdRef.current !== apiId) {
      stopPolling();
      snapshotPollSessionRef.current = null;
      orchestratedPackageApiIdRef.current = apiId;
    }

    if (!apiId || dataPackage?.isSnapshot !== true) {
      return;
    }

    const pct = dataPackage.percentSnapshotDone ?? 0;
    if (pct < 100) {
      const sessionKey = `${apiId}:incomplete`;
      if (snapshotPollSessionRef.current !== sessionKey) {
        snapshotPollSessionRef.current = sessionKey;
        startPolling(apiId);
      }
    } else if (snapshotPollSessionRef.current?.startsWith(`${apiId}:`)) {
      log.debug('[DataPackageDetails] Snapshot complete, stopping polling');
      stopPolling();
      snapshotPollSessionRef.current = null;
    }
  }, [selectedNode?.dataId, dataPackage?.isSnapshot, dataPackage?.percentSnapshotDone, startPolling, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Check if data package is published or submitted
  const isPublished = dataPackage?.status === StatusDto.Published || !!dataPackage?.doi;
  const isSubmitted = dataPackage?.status === StatusDto.Submitted;

  // Same rule as other entities: submitted or published packages are not editable in the UI.
  const canEdit = !(isSubmitted || isPublished);

  // Align with registry + API: first-time PATCH, or POST restart-snapshot when incomplete and stale (~10 min).
  const snapshotStaleForRestart =
    !!dataPackage?.isSnapshot &&
    dataPackage.status === StatusDto.Pending &&
    (dataPackage.percentSnapshotDone ?? 0) < 100 &&
    isSnapshotBuildStaleForRestart(dataPackage.lastChangedDate);

  const canCreateSnapshot =
    isAuthorized(PrivilegeEnum.DataSnapshot, 'create') &&
    dataPackage?.status === StatusDto.Pending &&
    (!dataPackage?.isSnapshot || snapshotStaleForRestart);

  const handleModification = (modified: Partial<DataPackageDto>) => {
    log.debug('[DataPackageDetails] handleModification called:', {
      modified,
      selectedNode: !!selectedNode,
      dataId: selectedNode?.dataId,
    });
    if (!selectedNode) {
      log.warn('[DataPackageDetails] handleModification called but selectedNode is null');
      return;
    }
    updateDataPartial(selectedNode.dataId, modified);
    setDataModified(selectedNode.dataId, true);

    // Clear validation errors when user starts typing
    if (modified.label !== undefined) {
      setTitleError(false);
      clearFieldError('label');
    }
    if (modified.description !== undefined) {
      setDescriptionError(false);
      clearFieldError('description');
    }
    if (modified.keywords !== undefined) {
      setKeywordsError(false);
      clearFieldError('keywords');
    }
    if (modified.collaborators !== undefined) {
      clearFieldError('collaboratorIds');
    }
    if (modified.synthesisInstruments !== undefined) {
      clearFieldError('synthesisInstrumentIds');
    }
  };

  // Clear field errors when user starts editing
  const clearFieldError = (fieldKey: string) => {
    if (fieldErrors[fieldKey]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!selectedNode || !dataPackage?.id) return;

    const saveValidation = validateRequiredToSave(
      createDataPackageSaveValidation({
        label: dataPackage.label,
        description: dataPackage.description,
        collaboratorIds: dataPackage.collaborators?.map((c) => c.id).filter((id): id is string => !!id) || [],
      })
    );
    setTitleError(!!saveValidation.fieldErrors.label);
    setDescriptionError(!!saveValidation.fieldErrors.description);
    setKeywordsError(false);
    setFieldErrors(saveValidation.fieldErrors);

    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields', 'error');
      return;
    }

    try {
      log.debug('[DataPackageDetails] Saving data package:', dataPackage);

      const updateRequest = toUpdateDataPackageRequest(dataPackage);
      // Call backend API to update the data package
      const updatedData = await backend.data.update(dataPackage.id.toString(), updateRequest);

      // Update the data in the store with the response from backend
      updateData(selectedNode.dataId, updatedData);

      // Mark as no longer modified and exit edit mode (manual Save)
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false);
      setFieldErrors({});

      showSnackbar('Data package details saved successfully', 'success');
    } catch (error) {
      log.error('[DataPackageDetails] Failed to save data package:', error);
      showSnackbar('Failed to save data package details', 'error');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedNode || !dataPackage?.id) return;

    try {
      log.debug('[DataPackageDetails] Deleting data package:', dataPackage.id);
      await backend.data.deletePending(dataPackage.id.toString());
      showSnackbar('Data package deleted successfully', 'success');

      // Navigate to the search/grid page
      router.push(`${systemConfig?.basePath}/data`);
    } catch (error) {
      log.error('[DataPackageDetails] Failed to delete data package:', error);
      showSnackbar('Failed to delete data package', 'error');
    }
  };

  // Handle cancel - reset changes
  const handleCancel = () => {
    if (!selectedNode) return;

    // Reset any pending changes and exit edit mode
    setDataModified(selectedNode.dataId, false);
    setIsManuallyEditing(false);

    // Clear any error states
    setTitleError(false);
    setDescriptionError(false);
    setKeywordsError(false);

    showSnackbar('Changes cancelled', 'info');
  };

  // Handle create snapshot - this will be triggered by the action handler via the context menu or footer
  const handleCreateSnapshot = async () => {
    if (!selectedNode || !canCreateSnapshot || !dataPackage?.id) return;

    try {
      if (snapshotStaleForRestart) {
        log.debug('[DataPackageDetails] Resuming stalled snapshot for:', dataPackage.id);
        await backend.data.restartSnapshot(dataPackage.id);
        showSnackbar('Snapshot build resumed', 'success');
        // POST restart does not return a body; merge audit fields so "Resume stalled snapshot" clears immediately
        // (eligibility uses lastChangedDate vs ~10 min stale threshold) until the next poll/full reload.
        mergeEntityFromServer(selectedNode.nodeId, {
          lastChangedDate: new Date(),
          ...(currentUserId ? { lastChangedByUser: currentUserId } : {}),
        } as Partial<NodeDataType>);
      } else {
        log.debug('[DataPackageDetails] Starting snapshot creation for:', dataPackage.id);
        await backend.data.createSnapshot(dataPackage.id);
        showSnackbar('Snapshot creation started', 'success');
      }
      startPolling(getDataPackageApiId(selectedNode.dataId));
    } catch (error) {
      log.error('[DataPackageDetails] Error creating or resuming snapshot:', error);
      showSnackbar('Failed to start or resume snapshot', 'error');
    }
  };

  // Handle submit — validate, confirm, and submit once via submitWithValidation (submitEntity performs PATCH …/submit and refresh).
  const handleSubmit = async (): Promise<void> => {
    if (!dataPackage || !selectedNode) return;

    const validationFields = createDataPackageSubmitValidation(toDataPackageSubmitValidationInput(dataPackage));

    const result = await submitWithValidation(selectedNode, validationFields, 'Data Package');
    if (!result.success) {
      if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
        setFieldErrors(result.fieldErrors);
        setIsManuallyEditing(true);
      }
      return;
    }
    setFieldErrors({});
  };

  // Reset submitted snapshot to pending — same handler as tree context menu (`reset` action).
  const handleResetToPending = async (): Promise<void> => {
    if (!selectedNode) return;
    await executeActionForNode(selectedNode, 'reset', backend);
  };

  // Handle publish - publish the data package
  const handlePublish = async () => {
    if (!selectedNode) return;
    await executeActionForNode(selectedNode, 'publish-data-package', backend);
  };

  if (!dataPackage) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading data package details...
        </Typography>
      </Box>
    );
  }

  if (optionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  const snapshotProgressRounded = Math.round(dataPackage.percentSnapshotDone ?? 0);
  const snapshotInProgressForFooter =
    !!dataPackage.snapshotInProgress || (dataPackage.isSnapshot === true && snapshotProgressRounded < 100);

  const synthesisInstrumentsForDisplay = resolveSelectedInstruments(
    dataPackage.synthesisInstruments,
    options.synthesisInstruments
  );
  const characterizationInstrumentsForDisplay = resolveSelectedInstruments(
    dataPackage.characterizationInstruments,
    options.characterizationInstruments
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6" gutterBottom>
            {dataPackage.isSnapshot ? 'Data Package Snapshot' : 'Data Package'}
          </Typography>

          {dataPackage.isSnapshot &&
          dataPackage.status === StatusDto.Published &&
          isAuthorized(PrivilegeEnum.DataPackage, 'update') &&
          !(dataPackage.doi?.trim() && dataPackage.publicationDoi?.trim()) ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              To assign or update the package or publication DOI, use <strong>Set DOI</strong> on this data package in
              the project tree (right-click the node). DOIs cannot be edited on this screen.
            </Alert>
          ) : null}

          {/* Title */}
          <DetailField
            label="Title"
            value={dataPackage.label}
            isReadOnly={isReadOnly || !canEdit}
            requiredToSubmit
            error={fieldErrors.label}
            width={{ width: '100%' }}
          >
            <DebouncedTextField
              value={dataPackage.label || ''}
              onUpdate={(value: string) => handleModification({ label: value })}
              updateMode="blur"
              fullWidth
              size="small"
              error={!!fieldErrors.label || titleError}
              helperText={fieldErrors.label || (titleError ? 'Title is required' : '')}
              slotProps={{ input: { readOnly: isReadOnly || !canEdit } }}
            />
          </DetailField>

          {/* DOI, Publication, Share Link, Collaborators */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* Share Link - Show for all non-published packages */}
            {dataPackage.status !== StatusDto.Published && !dataPackage.doi ? (
              <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {dataPackage.isSnapshot ? 'Pre-Publish Link' : 'Share Link'}
                </Typography>
                {dataPackage.prePublishId ? (
                  (() => {
                    const fullUrl = buildPublicDataPackageUrl(dataPackage.prePublishId, systemConfig?.basePath ?? '');

                    return (
                      <MuiLink
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, wordBreak: 'break-all' }}
                      >
                        {fullUrl}
                        <OpenInNew fontSize="small" />
                      </MuiLink>
                    );
                  })()
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      No share link generated yet
                    </Typography>
                    {canEdit ? (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={generatingShareLink}
                        onClick={async () => {
                          if (!dataPackage.id || !selectedNode?.dataId) return;
                          setGeneratingShareLink(true);
                          try {
                            await backend.data.generateShareId(dataPackage.id);
                            // Reload to show the new share ID
                            const updatedData = await backend.data.get(getDataPackageApiId(selectedNode.dataId));
                            updateData(selectedNode.dataId, updatedData);
                            showSnackbar('Share link generated successfully', 'success');
                          } catch (error) {
                            log.error('[DataPackageDetails] Error generating share link:', error);
                            showSnackbar('Failed to generate share link', 'error');
                          } finally {
                            setGeneratingShareLink(false);
                          }
                        }}
                      >
                        {generatingShareLink ? 'Generating...' : 'Generate Share Link'}
                      </Button>
                    ) : null}
                  </Box>
                )}
              </Box>
            ) : null}

            <DetailField
              label="DOI"
              value={dataPackage.doi ? <DoiReadonlyLink doi={dataPackage.doi} /> : 'None'}
              isReadOnly
            />

            <DetailField
              label="Publication"
              value={dataPackage.publicationDoi ? <DoiReadonlyLink doi={dataPackage.publicationDoi} /> : 'None'}
              isReadOnly
            />

            <DetailField
              label="Collaborators"
              value={
                isReadOnly || !canEdit
                  ? dataPackage.collaborators && dataPackage.collaborators.length > 0
                    ? (() => {
                        const collaboratorIds = dataPackage.collaborators
                          .map((c) => c.id)
                          .filter((id): id is string => !!id);
                        const collaboratorNames = collaboratorIds.map((id) => resolveUser(id));
                        return <UserLinks userIds={collaboratorIds} displayTexts={collaboratorNames} />;
                      })()
                    : 'None'
                  : dataPackage.collaborators?.length
                    ? `${dataPackage.collaborators.length} selected`
                    : 'None'
              }
              isReadOnly={isReadOnly || !canEdit}
              requiredToSubmit
              error={fieldErrors.collaboratorIds}
            >
              {(() => {
                // Merge staff and users, deduplicate by id, and sort alphabetically
                const allUsers = [...(options.staff || []), ...(options.users || [])];
                const uniqueUsers = Array.from(new Map(allUsers.map((user) => [user.id, user])).values()).sort(
                  (a, b) => {
                    const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
                    const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
                    return nameA.localeCompare(nameB);
                  }
                );

                return (
                  <Autocomplete
                    multiple
                    options={uniqueUsers}
                    getOptionLabel={formatCollaboratorLabel}
                    value={uniqueUsers.filter((user) => dataPackage.collaborators?.some((c) => c.id === user.id))}
                    onChange={(_, newValue) =>
                      handleModification({
                        collaborators: newValue.map((c) => ({ id: c.id })),
                      })
                    }
                    disabled={isReadOnly || !canEdit}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option.id} label={formatCollaboratorLabel(option)} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" placeholder="Select collaborators..." />
                    )}
                  />
                );
              })()}
            </DetailField>
          </Box>

          {/* Synthesis and Characterization Instruments */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <DetailField
              label="Synthesis Instruments"
              value={
                isReadOnly || !canEdit ? (
                  <InstrumentReadonlyList instruments={synthesisInstrumentsForDisplay} />
                ) : dataPackage.synthesisInstruments?.length ? (
                  `${dataPackage.synthesisInstruments.length} selected`
                ) : (
                  'None'
                )
              }
              isReadOnly={isReadOnly || !canEdit}
              requiredToSubmit={!!(dataPackage.samples && dataPackage.samples.length > 0)}
              error={fieldErrors.synthesisInstrumentIds}
            >
              <Autocomplete
                multiple
                options={options.synthesisInstruments || []}
                getOptionLabel={(option) => `${option.id} - ${option.name}`}
                value={
                  options.synthesisInstruments?.filter((i) =>
                    dataPackage.synthesisInstruments?.some((s) => s.id === i.id)
                  ) || []
                }
                onChange={(_, newValue) =>
                  handleModification({
                    synthesisInstruments: newValue.map((i) => ({ id: i.id })),
                  })
                }
                disabled={isReadOnly || !canEdit}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} label={`${option.id} - ${option.name}`} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" placeholder="Select synthesis instruments" />
                )}
              />
            </DetailField>

            <DetailField
              label="Characterization Instruments"
              value={
                isReadOnly || !canEdit ? (
                  <InstrumentReadonlyList instruments={characterizationInstrumentsForDisplay} />
                ) : dataPackage.characterizationInstruments?.length ? (
                  `${dataPackage.characterizationInstruments.length} selected`
                ) : (
                  'None'
                )
              }
              isReadOnly={isReadOnly || !canEdit}
            >
              <Autocomplete
                multiple
                options={options.characterizationInstruments || []}
                getOptionLabel={(option) => `${option.id} - ${option.name}`}
                value={
                  options.characterizationInstruments?.filter((i) =>
                    dataPackage.characterizationInstruments?.some((c) => c.id === i.id)
                  ) || []
                }
                onChange={(_, newValue) =>
                  handleModification({
                    characterizationInstruments: newValue.map((i) => ({ id: i.id })),
                  })
                }
                disabled={isReadOnly || !canEdit}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} label={`${option.id} - ${option.name}`} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" placeholder="Select characterization instruments" />
                )}
              />
            </DetailField>
          </Box>

          {/* Description */}
          <DetailField
            label="Description"
            value={<span dangerouslySetInnerHTML={{ __html: dataPackage.description as TrustedHTML }} />}
            isReadOnly={isReadOnly || !canEdit}
            width={{ width: '100%' }}
            requiredToSubmit
            error={fieldErrors.description}
          >
            <Box>
              <RichTextEditor
                value={dataPackage.description || ''}
                onChange={(content) => {
                  handleModification({ description: content });
                }}
              />
              {fieldErrors.description || descriptionError ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {fieldErrors.description || 'Description is required'}
                </Typography>
              ) : null}
            </Box>
          </DetailField>

          {/* Keywords */}
          <DetailField
            label="Keywords"
            value={dataPackage.keywords?.join(', ') || 'None'}
            isReadOnly={isReadOnly || !canEdit}
            width={{ width: '100%' }}
            requiredToSubmit
            error={fieldErrors.keywords}
          >
            <Box>
              <DebouncedTextField
                value={dataPackage?.keywords?.join(', ') || ''}
                onUpdate={(value: string) =>
                  handleModification({
                    keywords: value
                      .split(',')
                      .map((k) => k.trim())
                      .filter(Boolean),
                  })
                }
                updateMode="blur"
                fullWidth
                size="small"
                error={!!fieldErrors.keywords || keywordsError}
                helperText={fieldErrors.keywords}
                slotProps={{ input: { readOnly: isReadOnly || !canEdit } }}
                placeholder="Keywords for the data package (comma-separated)"
              />
              {fieldErrors.keywords || keywordsError ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {fieldErrors.keywords || 'At least one keyword is required'}
                </Typography>
              ) : null}
            </Box>
          </DetailField>
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        <DataPackageFooter
          lastChangedDate={dataPackage.lastChangedDate}
          lastChangedBy={dataPackage.lastChangedByUser || 'Unknown'}
          status={dataPackage.status || 'Draft'}
          onSave={handleSave}
          onDelete={handleDelete}
          onCancel={handleCancel}
          isReadOnly={isReadOnly}
          setIsReadOnly={(val) => setIsManuallyEditing(!val)}
          privilege={PrivilegeEnum.DataPackage}
          nodeId={selectedNode?.dataId || ''}
          canCreateSnapshot={canCreateSnapshot}
          isResumeSnapshot={snapshotStaleForRestart}
          onCreateSnapshot={handleCreateSnapshot}
          hasChanges={selectedNode ? isDataModified(selectedNode.dataId) : false}
          isSnapshot={dataPackage.isSnapshot}
          snapshotInProgress={snapshotInProgressForFooter}
          snapshotProgress={snapshotProgressRounded}
          onSubmit={handleSubmit}
          onPublish={handlePublish}
          onResetToPending={handleResetToPending}
          canEditDetails={canEdit}
          nbSamples={dataPackage.nbSamples}
        />
      </Box>
    </Box>
  );
};

export default DataPackageDetails;
